import { Button, HStack, Text } from '@chakra-ui/react';
import { MaterialIcon } from '../../../ui/MaterialIcon';
import type { CreateDestination } from '../destination/createDestination';

interface CreateDestinationChipProps {
  readonly destination: CreateDestination;
  /** "From current view" when the destination is what the page shows. */
  readonly fromView: boolean;
  /** Absent when the entry point fixed the destination. */
  readonly onChange?: () => void;
  /** Step 2's sentence form: "{verb} {destination} · Change". */
  readonly verb?: string;
  /** Said instead of the usual hint (why the collection in view is not the default). */
  readonly note?: string;
}

function hint(destination: CreateDestination, fromView: boolean): string | null {
  if (destination.type === 'project' && destination.privacyMode) return 'Private';
  if (destination.type === 'project' && destination.parentName) {
    return `Inside ${destination.parentName}`;
  }
  return fromView ? 'From current view' : null;
}

/**
 * Where the new thing will be saved, shown on every step of the modal: a
 * "Save to" chip on step 1, a "{verb} {destination}" line on step 2. Both
 * open the picker. Nothing is ever created somewhere the user was not shown.
 */
export function CreateDestinationChip({
  destination,
  fromView,
  onChange,
  verb,
  note: override,
}: CreateDestinationChipProps) {
  const icon = destination.type === 'workspace' ? 'home' : 'folder_open';
  const note = override ?? hint(destination, fromView);
  if (verb) {
    return (
      <HStack spacing={1} fontSize="sm" color="text.secondary" flexWrap="wrap">
        <Text as="span">{verb}</Text>
        <Text as="span" fontWeight="semibold" color="text.primary" noOfLines={1}>
          {destination.name}
        </Text>
        {onChange && (
          <>
            <Text as="span" aria-hidden>
              ·
            </Text>
            <Button variant="link" size="sm" onClick={onChange}>
              Change
            </Button>
          </>
        )}
      </HStack>
    );
  }
  return (
    <HStack spacing={2} minW={0}>
      <Button
        variant="outline"
        size="sm"
        borderRadius="full"
        maxW={{ base: '60vw', md: '280px' }}
        isDisabled={!onChange}
        onClick={onChange}
        aria-label={`Save to ${destination.name}. Change where it is saved`}
        leftIcon={<MaterialIcon name={icon} boxSize={4} color="text.secondary" />}
        rightIcon={onChange ? <MaterialIcon name="expand_more" boxSize={4} /> : undefined}
      >
        <Text as="span" noOfLines={1}>
          {destination.name}
        </Text>
      </Button>
      {note && (
        <HStack spacing={1} color="text.secondary" minW={0}>
          {note === 'Private' && <MaterialIcon name="lock" boxSize={3.5} />}
          <Text fontSize="xs" noOfLines={1}>
            {note}
          </Text>
        </HStack>
      )}
    </HStack>
  );
}
